import { useState } from 'react';
import { Check, Users } from 'lucide-react';
import { Button } from '@/components/ui';
import { OnboardingLayout } from './OnboardingLayout';
import { ONBOARDING_COPY } from './onboardingCopy';

export interface JoinWaitStateProps {
  /** The signed-in account's email — the address the invite will arrive at. */
  email: string;
  /** The path switch. Idempotent in the container (create-or-resume). Rejecting shows the error line. */
  onSetUpFamily: () => Promise<void>;
  onSignOut: () => void;
  /** The chevron: back to the fork. */
  onBack: () => void;
}

/**
 * The join wait-state (YAR-438 / YAR-439, Figma "Join household / default"
 * 10631:35). A helper who is waiting for their invite. Static, and it NEVER
 * creates a household: the only thing it can start is the path switch, and
 * the container decides what that does.
 *
 * Geometry: 64px outline circle (`border/default`, token `border-strong`)
 * with the 28px `icon/primary` users glyph (the Badge icon-chip recipe as a
 * one-off frame) · 40px · H4 + blurb 16px apart · 20px · two check rows 8px
 * apart, each a 20px `icon/primary` check flush with the top of its 24px
 * line · 20px · reassurance 14/20 caption · the two exits 16px apart at the
 * bottom. The email is inline text that wraps — no truncation exists in the
 * design and none is added here (AC 10). It is the session's email, so a
 * mismatch with an invite "self-corrects" by definition (AC 8). Its colour
 * is `text/content/heading`: the Figma paragraph is body but the email span
 * inside it is bound to the heading token, so it stays darker than the line
 * around it on purpose (QA read the paragraph's #2f2f2f; the span is not).
 */
export function JoinWaitState({
  email,
  onSetUpFamily,
  onSignOut,
  onBack,
}: JoinWaitStateProps) {
  const copy = ONBOARDING_COPY.waitState;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSetUp = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSetUpFamily();
    } catch {
      setError(ONBOARDING_COPY.fork.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <OnboardingLayout
      showBackButton
      onBack={onBack}
      showProgressBar={false}
      stickyFooter={
        <div className="flex flex-col gap-4">
          {error && (
            <p
              role="alert"
              className="text-xs text-text-error"
            >
              {error}
            </p>
          )}
          {/* Figma draws this secondary on `border/neutral/default-subtle`
              (#e5e5e5), one step lighter than the Button variant's
              `border-strong`. Instance override rather than a variant
              change: 20-odd other secondaries were verified at #d1d1d1. */}
          <Button
            variant="secondary"
            fullWidth
            loading={busy}
            disabled={busy}
            onClick={() => void handleSetUp()}
            className="!border-border"
          >
            {copy.setUpInstead}
          </Button>
          <Button
            variant="tertiary"
            fullWidth
            disabled={busy}
            onClick={onSignOut}
          >
            {copy.signOut}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-10">
        <div
          aria-hidden
          className="flex size-16 items-center justify-center rounded-full border border-border-strong bg-white"
        >
          <Users className="size-7 text-icon-primary" />
        </div>
        <div className="flex flex-col gap-4">
          <h1 className="text-[28px] leading-8 font-bold text-text-hero">
            {copy.title}
          </h1>
          <p className="text-base leading-6 text-foreground">{copy.blurb}</p>
        </div>
        <div className="flex flex-col gap-5">
          <ul className="flex flex-col gap-2">
            <li className="flex items-start gap-2">
              <Check
                aria-hidden
                className="size-5 shrink-0 text-icon-primary"
              />
              <p className="flex-1 min-w-0 text-base leading-6 text-foreground [overflow-wrap:anywhere]">
                {copy.checkInvitePrefix}
                <span className="font-semibold text-text-heading">{email}</span>
              </p>
            </li>
            <li className="flex items-start gap-2">
              <Check
                aria-hidden
                className="size-5 shrink-0 text-icon-primary"
              />
              <p className="flex-1 min-w-0 text-base leading-6 text-foreground">
                {copy.checkTap}
              </p>
            </li>
          </ul>
          <p className="text-sm leading-5 text-muted-foreground">
            {copy.reassurance}
          </p>
        </div>
      </div>
    </OnboardingLayout>
  );
}
